// Tells the shared bundle which surface it is running on. The web app and the Chrome
// extension new-tab page are the same code; only storage and the API origin differ.

export const IS_EXTENSION = import.meta.env.VITE_BUILD_TARGET === "extension";

/** True when the chrome.storage API is actually reachable (i.e. really inside an extension). */
export const HAS_CHROME_STORAGE = typeof chrome !== "undefined" && Boolean(chrome?.storage?.local);

/**
 * Where the API lives.
 *   web  — same origin ("" → /api/...), which Vite proxies to :8787 in development.
 *          Override with VITE_API_URL only when the site and API are on different hosts.
 *   ext  — always absolute. Set VITE_EXT_API_URL before `npm run build:extension`.
 */
export const API_BASE = (() => {
  const trim = (v?: string) => v?.trim().replace(/\/$/, "") || "";
  if (IS_EXTENSION) {
    // Dev fallback so an unconfigured extension build still talks to a local server.
    return (
      trim(import.meta.env.VITE_EXT_API_URL) ||
      trim(import.meta.env.VITE_API_URL) ||
      "http://localhost:8787"
    );
  }
  return trim(import.meta.env.VITE_API_URL);
})();

export const apiUrl = (path: string) => `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;

/**
 * Absolute URL for an asset the API serves (wallpaper uploads). Extension pages have no
 * useful relative base, so /uploads/... has to be expanded there.
 */
export const assetUrl = (url: string | null | undefined): string => {
  if (!url) return "";
  if (/^(https?:|data:|blob:)/.test(url)) return url;
  if (url.startsWith("/uploads/")) return apiUrl(url);
  return url;
};

/**
 * Yerel kimlik üretici. `crypto.randomUUID()` yalnızca güvenli origin'lerde (https,
 * localhost, chrome-extension) tanımlı; site şu an düz HTTP üzerinden de servis edildiği
 * için orada çağrı hata veriyor ve çağıran işlem sessizce kırılıyordu.
 */
export function localId(): string {
  const c = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID();
  if (c?.getRandomValues) {
    const bytes = c.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Uygulama sürümü — tek kaynak `extension/manifest.json`.
 *
 * "Hakkında" bölümünde elle yazılmış "1.0.0" duruyordu ve paket 1.0.5'e gelmişken
 * kullanıcıya hâlâ 1.0.0 gösteriyordu. Eklentide manifest çalışma anında okunuyor,
 * sitede derleme anında gömülüyor (VITE_APP_VERSION); ikisi de aynı dosyadan geliyor.
 */
export const APP_VERSION: string = (() => {
  try {
    const fromManifest = chrome?.runtime?.getManifest?.().version;
    if (fromManifest) return fromManifest;
  } catch {
    /* eklenti değiliz */
  }
  return import.meta.env.VITE_APP_VERSION ?? "0.0.0";
})();
